import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import { FilterService } from '@primevue/core/api'
import { containsName } from '@/lib/search'
import Aura from '@primevue/themes/aura'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'

import Button from 'primevue/button'
import Card from 'primevue/card'
import Divider from 'primevue/divider'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Password from 'primevue/password'
import ProgressSpinner from 'primevue/progressspinner'
import Select from 'primevue/select'
import Skeleton from 'primevue/skeleton'
import Tag from 'primevue/tag'
import Toast from 'primevue/toast'

import 'primeicons/primeicons.css'
import '@/assets/base.css'

import App from '@/App.vue'
import router from '@/router'
import { setAuthFailureHandler } from '@/api/client'
import { bgLocale } from '@/lib/locale'
import { useAuth } from '@/stores/auth'
import { useCompanies } from '@/stores/companies'

// Shared by all searchable Select/MultiSelect/Listbox controls, regardless of label field.
FilterService.register('contains', containsName)

const app = createApp(App)

app.use(createPinia())
app.use(router)

// Темата е стандартната Aura: системата изглежда като PrimeVue, защото
// собственият дизайн не е това, което клиентът купува.
//
// `darkModeSelector: false` изключва тъмния вариант. По подразбиране Aura следва
// настройката на операционната система, а тъмната палитра никога не е била
// проектирана тук: плочите и панелите излизаха черно върху черно. Системата
// замества бели листове в Excel и се гледа цял ден — светла е и остава светла,
// вместо да се поддържат две палитри, от които едната никой не е поръчвал.
app.use(PrimeVue, {
  theme: { preset: Aura, options: { darkModeSelector: false } },
})
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

// Слива се върху вградената локализация, за да останат преведени и ключовете,
// които не сме изброили.
const primevue = app.config.globalProperties.$primevue
if (primevue?.config?.locale) {
  Object.assign(primevue.config.locale, bgLocale)
}

app.component('Button', Button)
app.component('Card', Card)
app.component('Divider', Divider)
app.component('IconField', IconField)
app.component('InputIcon', InputIcon)
app.component('InputText', InputText)
app.component('Message', Message)
app.component('Password', Password)
app.component('ProgressSpinner', ProgressSpinner)
app.component('Select', Select)
app.component('Skeleton', Skeleton)
app.component('Tag', Tag)
app.component('Toast', Toast)

// Изтекла сесия на произволна заявка връща човека на екрана за вход, със
// запомнен адрес, за да продължи оттам.
setAuthFailureHandler(() => {
  const auth = useAuth()
  const companies = useCompanies()
  const current = router.currentRoute.value

  auth.clear()
  companies.reset()

  if (!current.meta.public) {
    void router.replace({
      name: 'login',
      query: current.fullPath === '/' ? {} : { redirect: current.fullPath },
    })
  }
})

// Resolve the layout before mounting: otherwise a fresh public link briefly
// mounts AppLayout, whose mounted hook loads the office's companies.
void router.isReady().then(() => app.mount('#app'))
